import { useAuth } from '@commander/auth';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../../utils/Constant';

/**
 * Landing page for an admin-initiated megamode session.
 *
 * The admin app opens this route in a new tab with the megamode token in
 * the URL fragment (`/megamode#token=...`). We exchange it via
 * `/auth/token-login` for the full user payload (with a freshly re-minted
 * megamode token), establish the session, and land on the dashboard.
 *
 * The token-login call sends the megamode token EXPLICITLY (raw fetch,
 * not the shared API client) so it can't be shadowed by a pre-existing
 * auth cookie/localStorage entry — on localhost, cookies are shared across
 * ports, so the admin's own session could otherwise win.
 */
export function MegaModeLanding() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const token = params.get('token');

    // Remove the token from the URL so it doesn't linger in history.
    window.history.replaceState(null, '', window.location.pathname);

    if (!token) {
      setError('Missing MegaMode token.');
      return;
    }

    if (!BASE_URL) {
      setError('Configuration error: API base URL is not set.');
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/auth/token-login`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const body = await res.json().catch(() => ({}));

        if (!res.ok) {
          // eslint-disable-next-line no-console
          console.error('[MegaMode] token-login failed', res.status, body);
          setError(`MegaMode failed (${res.status}): ${body?.message || 'unknown error'}`);
          return;
        }

        await login({ status: 200, data: body });
        navigate('/dashboard', { replace: true });
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error('[MegaMode] token-login error', err);
        setError(`MegaMode failed: ${err?.message || 'network error'}`);
      }
    })();
  }, [login, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        {error ? (
          <p className="text-destructive">{error}</p>
        ) : (
          <>
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Entering Read-Only MegaMode…</p>
          </>
        )}
      </div>
    </div>
  );
}
